import type { ScreenShareTile } from './roomState';

/**
 * The stage-and-rail selection model of the WEBINAR broadcast surface (CR: every share
 * visible): the stage shows the dominant share (never cropped, object-contain) and every
 * OTHER live share renders as a real tile in the stage's side rail - each with a pin button
 * that moves it onto the stage. Nothing is ever demoted to a preview-only thumb, so two
 * concurrent shares are both visible at once. (The MEETING room packs shares into the unified
 * adaptive grid instead - newest share dominant, the rest ordinary tiles; see dominant.ts and
 * MeetingLayout.tsx. The same pin > newest-share dominance maps here onto stage + rail.)
 */

/** The stage-and-rail selection for the current set of live shares. */
export interface ShareStageModel {
  /** The share rendered on the stage. */
  readonly stage: ScreenShareTile;
  /** Every other live share, in derivation order; a rail tile's pin moves it onto the stage. */
  readonly rail: readonly ScreenShareTile[];
}

/**
 * Tracks share arrivals across renders, MOST RECENT FIRST: identities already known keep
 * their relative order, identities that disappeared drop out, unseen ones prepend in
 * derivation order (simultaneous newcomers tie among themselves). Pure and idempotent -
 * safe to run on every render and under StrictMode double-invocation. The list feeds
 * {@link resolveShareStage}'s recency default: the first live hit is the newest share.
 */
export function trackArrivals(
  known: readonly string[],
  identities: readonly string[],
): readonly string[] {
  const live = new Set(identities);
  const kept = known.filter((identity) => live.has(identity));
  const seen = new Set(kept);
  const newcomers: string[] = [];
  for (const identity of identities) {
    if (!seen.has(identity)) {
      newcomers.push(identity);
      seen.add(identity);
    }
  }
  if (newcomers.length === 0 && kept.length === known.length) {
    // Nothing changed: the same reference lets callers' setState bail out instead of
    // looping (the identities input is a fresh array on every parent render).
    return known;
  }
  return [...newcomers, ...kept];
}

/**
 * Resolves the stage-and-rail model for the live shares.
 *
 * Stage pick order: an explicit rail pin (pickedIdentity) wins while that share stays
 * live; otherwise the most recently started share wins (recentFirst, most recent first);
 * otherwise the derivation's first share (the caller tracks arrivals, so this only covers
 * the very first render). Returns null when nothing is shared - the caller renders the
 * normal grid. The local participant's own share stays OUT of the rail while it is the
 * stage (a self-tile beside the self-stage is noise); when a remote share owns the stage,
 * the own share appears as a clearly marked rail tile (the UI labels it «you») so the
 * sharer can switch back to their own screen.
 */
export function resolveShareStage(
  shares: readonly ScreenShareTile[],
  pickedIdentity: string | null,
  recentFirst: readonly string[],
): ShareStageModel | null {
  if (shares.length === 0) {
    return null;
  }
  const byIdentity = new Map(
    shares.map((share): [string, ScreenShareTile] => [share.identity, share]),
  );
  const stage =
    (pickedIdentity !== null ? byIdentity.get(pickedIdentity) : undefined) ??
    recentFirst
      .map((identity) => byIdentity.get(identity))
      .find((share): share is ScreenShareTile => share !== undefined) ??
    shares[0];
  if (stage === undefined) {
    return null;
  }
  const rail = shares.filter((share) => share.identity !== stage.identity);
  return { stage, rail };
}
